import { Box, Button, IconButton, Typography } from "@mui/material";
import AppBarContainer from "../../components/shared/AppBarContainer";
import PageContentContainer from "../../components/shared/PageContentContainer";
import { useCallback, useEffect, useState } from "react";
import { BalootUserAPIs } from "../../api/APIs";
import { useRequest } from "../../api/utils/useRequest";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import BottomFullWidthButtonContainer from "../../components/shared/BottomFullWidthButtonContainer";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { toBilionToman, toToman } from "../../utils/Utils";
import BalootAreaChart from "../../components/shared/BalootAreaChart";
import moment from "moment-jalaali";
import { FinancialAssetSubTypeFarsi } from "../../types/FinancialAssets";
import GrowthPercentText from "../../components/shared/GrowthPercentText";
import CustomRectangularSkeleton from "../../components/shared/CustomRectangularSkeleton";
import CustomTextSkeleton from "../../components/shared/CustomTextSkeleton";
import { ActionModal } from "../../components/shared/ActionModal";
import { LoadingButton } from "@mui/lab";

function FinancialAssetPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const assetName = location.state.assetName;
  const { assetId } = params;
  const [periodIndex, setPeriodIndex] = useState(0);
  const [openModal, setOpenModal] = useState(false);

  const {
    loading,
    result,
    apiCall: getFinancialAsset,
  } = useRequest(BalootUserAPIs.getFinancialAsset, true);

  const { loading: sejamStatusLoading, apiCall: getUserSejamStatus } =
    useRequest(BalootUserAPIs.getUserSejamStatus);

  const getSejamStatus = () => {
    getUserSejamStatus({
      onSuccess: (response) => {
        if (response.status === "AUTHENTICATED") {
          navigate(`/app/financial-asset/${assetId}/buy`);
        } else {
          setOpenModal(true);
        }
      },
    });
  };

  useEffect(() => {
    getFinancialAsset({ data: { assetId } });
  }, []);

  const getPeriodData = useCallback(
    (index, data) => {
      const now = moment().unix() * 1000;
      let fromDate =
        moment()
          .subtract(index + 1, "jMonth")
          .unix() * 1000;
      const result = data.filter((item) => {
        const itemDate = new Date(item.date).getTime();
        return itemDate <= now && itemDate >= fromDate;
      });
      return result;
    },
    [periodIndex, result]
  );

  return (
    <>
      <AppBarContainer
        rightElement={
          <IconButton
            onClick={() => {
              navigate(-1);
            }}
          >
            <ArrowForward sx={{ color: "common.white" }} />
          </IconButton>
        }
        centerElement={
          <Typography variant="h5" color={"common.white"}>
            {assetName}
          </Typography>
        }
      />
      <Box
        sx={{
          width: "100%",
          position: "relative",
          zIndex: 1,
          mb: "-50px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: 64,
            height: 64,
            boxShadow: (theme) => theme.shadows[2],
            borderRadius: "14px",
            bgcolor: "common.white",
          }}
        >
          {loading ? (
            <CustomRectangularSkeleton width={42} height={42} />
          ) : (
            result && <img src={result.asset.iconPath} width={42} height={42} />
          )}
        </Box>
      </Box>
      <PageContentContainer
        sx={{
          mt: "20px",
          overflow: "auto",
          pt: 4,
          pb: `calc(env(safe-area-inset-bottom) + 112px )`,
          display: "block",
        }}
      >
        {loading ? (
          <>
            <CustomTextSkeleton sx={{ mt: 1 }} />
            <CustomTextSkeleton sx={{ mt: 1 }} />
            <CustomRectangularSkeleton sx={{ mt: 2 }} height={200} />
            <CustomRectangularSkeleton sx={{ mt: 3 }} height={70} />
            <CustomTextSkeleton sx={{ mt: 1 }} />
            <CustomTextSkeleton sx={{ mt: 1 }} />
          </>
        ) : (
          result && (
            <>
              <ActionModal
                open={openModal}
                setOpen={setOpenModal}
                title="فعال‌سازی سجام"
                description="برای خرید صندوق‌های سرمایه‌گذاری، باید سجامی باشید. با لمس دکمه «فعال‌سازی» می‌توانید سجام خود را فعال کنید."
                isContained
                actionTitle="فعال‌سازی"
                action={() => navigate("/app/profile/setting/sejam")}
                cancelTitle="بستن"
                cancelAction={() =>
                  navigate(`/app/financial-asset/${assetId}/buy`)
                }
              />
              {[
                {
                  title: "ارزش سرمایه گذاری فعلی شما (تومان)",
                  value: toToman(result.invested.totalAmount),
                },
                { title: "تعداد واحد", value: result.invested.unit },
              ].map((item) => {
                return (
                  <Box
                    key={item.title}
                    sx={{
                      my: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="subtitle1" color="grey.2">
                      {item.title}
                    </Typography>
                    <Typography variant="body1" color="grey.2">
                      {item.value}
                    </Typography>
                  </Box>
                );
              })}
              <BalootAreaChart data={getPeriodData(periodIndex, result.data)} />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {[1, 3, 6].map((item, index) => {
                  const isActive = index === periodIndex;
                  return (
                    <Box
                      key={item}
                      onClick={() => setPeriodIndex(index)}
                      sx={{
                        bgcolor: isActive ? "grey.2" : "common.white",
                        borderRadius: "16px",
                        mx: "2px",
                        minWidth: 60,
                        p: "4px",
                      }}
                    >
                      <Typography
                        align="center"
                        variant="body2"
                        color={isActive ? "common.white" : "grey.3"}
                      >
                        {`${item}ماه`}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box
                sx={{
                  mt: 3,
                  py: "16px",
                  px: "12px",
                  bgcolor: "grey.7",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {[
                  {
                    title: "نوع صندوق",
                    value: FinancialAssetSubTypeFarsi[result.asset.subType],
                  },
                  {
                    title: " ارزش کل دارایی (میلیارد تومان)",
                    value: toBilionToman(
                      result.asset.characteristics.fundCapital
                    ),
                  },
                  {
                    title: "دوره ماندگاری",
                    value: result.asset.characteristics.holdingPeriod,
                  },
                ].map((item) => {
                  return (
                    <Box
                      key={item.title}
                      sx={{
                        my: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="subtitle1" color="grey.2">
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="grey.2">
                        {item.value}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Box
                sx={{
                  mt: 3,
                  py: "16px",
                  px: "12px",
                  bgcolor: "grey.7",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="grey.2">
                  نرخ‌ها
                </Typography>
                {result.rates.map((item) => {
                  return (
                    <Box
                      key={item.key}
                      sx={{
                        my: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="subtitle1" color="grey.2">
                        {item.type === "PERCENT"
                          ? item.key
                          : `${item.key} (تومان)`}
                      </Typography>
                      {item.type === "PERCENT" ? (
                        <GrowthPercentText rate={item.value} />
                      ) : (
                        <Typography variant="body2" color="grey.2">
                          {toToman(item.value)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </>
          )
        )}
        <BottomFullWidthButtonContainer>
          <LoadingButton
            loading={sejamStatusLoading}
            disabled={!result}
            onClick={() => {
              navigate(`/app/financial-asset/${assetId}/buy`);
            }}
            sx={{
              color: "common.white",
              typography: "h3",
            }}
            variant="contained"
            fullWidth
          >
            درخواست خرید
          </LoadingButton>
          {(!result || result.invested.unit > 0) && (
            <>
              <Box width={16}></Box>
              <LoadingButton
                disabled={!result}
                onClick={() => {
                  navigate(`/app/financial-asset/${assetId}/sell`);
                }}
                color="error"
                sx={{
                  borderRadius: "12px",
                  typography: "h3",
                }}
                variant="outlined"
                fullWidth
              >
                درخواست فروش
              </LoadingButton>
            </>
          )}
        </BottomFullWidthButtonContainer>
      </PageContentContainer>
    </>
  );
}

export default FinancialAssetPage;
